import React, { useRef, useState, useEffect } from 'react';
import Select from 'react-select';
import { Formik, Form, Field, FieldArray, FieldProps } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Program, ApiObject, CommonHash, StudyOption, IntakeDate, DeadlineDate } from 'app/types';

import Text from 'common/components/Text/Text';
import {
  useGetBackofficeNewProgramQuery,
  useAddProgramMutation,
  usePatchProgramMutation,
  useGetSchoolsSearchQuery,
  useGetBackofficeProgramQuery,
  useGetCitiesSearchQuery,
} from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';
import styles from './ProgramForm.module.scss';
interface IntakeDateCompProps {
  name: string;
  studyOption: CommonHash;
}
interface DeadlineDateCompProps {
  name: string;
  intakeDate: CommonHash;
}
interface DateProps {
  date: string;
}

const DatePickerField = ({ field }: DateProps & FieldProps): React.ReactElement => {
  const dateInputRef = useRef(null);
  return (
    <div className={styles.group}>
      <label> Date </label>
      <input type="date" ref={dateInputRef} {...field} />
    </div>
  );
};

const DeadlineDatesForm = ({ name, intakeDate }: DeadlineDateCompProps): React.ReactElement => {
  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div>
          {intakeDate.deadline_dates && intakeDate.deadline_dates.length ? (
            intakeDate.deadline_dates.map((d_date: DeadlineDate, index: number) => (
              <div key={index}>
                <div className="d-flex gap-3 align-items-center mt-2">
                  <Text size={22}> Deadline Date {index + 1} </Text>
                  <Button
                    type="button"
                    variant="danger"
                    className={styles.deleteButton}
                    onClick={() => {
                      arrayHelpers.remove(index);
                    }}
                  >
                    Delete
                  </Button>
                </div>
                <div className={styles.group}>
                  <Field name={`${name}[${index}].date`} component={DatePickerField} />
                </div>
                {index === intakeDate.deadline_dates.length - 1 && (
                  <div>
                    <Button
                      type="button"
                      variant="success"
                      className="mt-3"
                      onClick={() =>
                        arrayHelpers.push({
                          date: '',
                          description: '',
                        })
                      }
                    >
                      + Deadline date
                    </Button>
                    <hr />
                  </div>
                )}
              </div>
            ))
          ) : (
            <Button
              type="button"
              variant="success"
              className="mt-3"
              onClick={() =>
                arrayHelpers.push({
                  date: '',
                  description: '',
                })
              }
            >
              + Deadline date
            </Button>
          )}
        </div>
      )}
    />
  );
};

const IntakeDatesForm = ({ name, studyOption }: IntakeDateCompProps): React.ReactElement => {
  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div>
          {studyOption.intake_dates && studyOption.intake_dates.length ? (
            studyOption.intake_dates.map((i_date: IntakeDate, index: number) => (
              <div key={index}>
                <Text size={22}> Intake date {index + 1} </Text>
                <div className="d-flex gap-3 align-items-center mt-2">
                  <Field name={`${name}[${index}].date`} component={DatePickerField} />
                  <Button
                    type="button"
                    variant="danger"
                    className={styles.deleteButton}
                    onClick={() => {
                      arrayHelpers.remove(index);
                    }}
                  >
                    Delete
                  </Button>
                </div>
                <DeadlineDatesForm name={`${name}[${index}].deadline_dates]`} intakeDate={i_date} />
                {index === studyOption.intake_dates.length - 1 && (
                  <Button
                    type="button"
                    className="mt-3"
                    variant="success"
                    onClick={() =>
                      arrayHelpers.push({
                        date: '',
                        deadline_dates: [
                          {
                            date: '',
                            description: '',
                          },
                        ],
                      })
                    }
                  >
                    + Intake date
                  </Button>
                )}
              </div>
            ))
          ) : (
            <Button
              type="button"
              className="mt-3"
              variant="success"
              onClick={() =>
                arrayHelpers.push({
                  date: '',
                  deadline_dates: [
                    {
                      date: '',
                      description: '',
                    },
                  ],
                })
              }
            >
              Add a Intake Date
            </Button>
          )}
        </div>
      )}
    />
  );
};
const ProgramForm = (): React.ReactElement => {
  const { id } = useParams();
  let program: ApiObject<Program> | undefined;
  let isProgramLoading;
  if (id) {
    ({ data: program, isLoading: isProgramLoading } = useGetBackofficeProgramQuery(parseInt(id)));
  } else {
    ({ data: program, isLoading: isProgramLoading } = useGetBackofficeNewProgramQuery());
  }
  const { data: { data: cities } = {}, isLoading: citiesLoading } = useGetCitiesSearchQuery();
  const [addProgram, { data: newProgram }] = useAddProgramMutation();
  const [patchProgram] = usePatchProgramMutation();
  const navigate = useNavigate();
  const { data: { data: schools } = {}, isLoading: schoolsLoading } = useGetSchoolsSearchQuery();
  const initialValues: Program = { ...(program?.data || ({} as Program)) };
  const [school, setSchool] = useState(
    program?.data.school_id
      ? {
          label: schools?.find((c) => c.id === program?.data.school_id)?.name || '',
          value: program?.data.school_id,
        }
      : 'Search city'
  );

  useEffect(() => {
    if (program?.data.school_id) {
      setSchool({
        label: schools?.find((c) => c.id === program?.data.school_id)?.name || '',
        value: program?.data.school_id,
      });
    }
  }, [program?.data.school_id, schools]);

  if (isProgramLoading) return <Loader />;
  return (
    <div>
      <h1>{id ? `Editing ${program?.data.name}` : 'New program'}</h1>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          if (id) {
            await patchProgram({ program_id: id, data: values });
            navigate(`/backoffice/programs/${id}`);
          } else {
            const result = await addProgram(values);
            if (result) {
              navigate(`/backoffice/programs`);
              // navigate(`/backoffice/programs/${result.data.id}`);
            }
          }
        }}
      >
        {({ setFieldValue, values, handleChange }) => (
          <Form className={styles.form}>
            <div className={styles.group}>
              <label htmlFor="program[name]">Name</label>
              <input
                name="program[name]"
                placeholder="Name"
                onChange={handleChange}
                defaultValue={program?.data.name || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[school_id]">School</label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={styles.filtersFormSelect}
                placeholder="Search School"
                options={schools?.map((c) => ({ label: c.name, value: c.id })) || []}
                isLoading={schoolsLoading}
                onChange={(school: any) => {
                  setSchool(school);
                  setFieldValue('program[school_id]', school.value);
                }}
                value={school}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[web_page]">Web Page</label>
              <input
                name="program[web_page]"
                placeholder="Web Page"
                defaultValue={program?.data.web_page || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[overview]">Overview</label>
              <textarea
                name="program[overview]"
                placeholder="Overview"
                defaultValue={program?.data.overview || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[average_age]">Average age</label>
              <input
                name="program[average_age]"
                type="number"
                placeholder="Average age"
                defaultValue={program?.data.average_age || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[international_student_percentage]">
                International Student Percentage
              </label>
              <input
                name="program[international_student_percentage]"
                type="number"
                placeholder="International Student Percentage"
                defaultValue={program?.data.international_student_percentage || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[average_salary]">Average salary</label>
              <input
                name="program[average_salary]"
                type="number"
                placeholder="Average salary"
                defaultValue={program?.data.average_salary || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[salary_increase]">Salary increase</label>
              <input
                name="program[salary_increase]"
                type="number"
                placeholder="Salary increase"
                defaultValue={program?.data.salary_increase || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[offer_acceptance_percentage]">
                Offer acceptance percentage
              </label>
              <input
                name="program[offer_acceptance_percentage]"
                type="number"
                placeholder="Offer acceptance percentage"
                defaultValue={program?.data.offer_acceptance_percentage || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[work_experience_min]">Work experience min</label>
              <input
                name="program[work_experience_min]"
                type="number"
                placeholder="Work experience min"
                defaultValue={program?.data.work_experience_min || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[work_experience_avg]">Work experience avg</label>
              <input
                name="program[work_experience_avg]"
                type="number"
                placeholder="Work experience avg"
                defaultValue={program?.data.work_experience_avg || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[work_experience_max]">Work experience max</label>
              <input
                name="program[work_experience_max]"
                type="number"
                placeholder="Work experience max"
                defaultValue={program?.data.work_experience_max || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[relevant_degree_require]">Relevant_degree_require</label>
              <select onChange={handleChange} name="program[relevant_degree_require]">
                <option
                  selected={String(program?.data.relevant_degree_require) === 'nil'}
                  value="nil"
                />
                <option
                  selected={String(program?.data.relevant_degree_require) === 'true'}
                  value="true"
                >
                  True
                </option>
                <option
                  selected={String(program?.data.relevant_degree_require) === 'false'}
                  value="false"
                >
                  False
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[why_choose_this_program]">Why choose this program</label>
              <textarea
                name="program[why_choose_this_program]"
                placeholder="Why choose this program"
                defaultValue={program?.data.why_choose_this_program || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[teaching_and_modules]">Teaching and modules</label>
              <Field
                name="program[teaching_and_modules]"
                as="textarea"
                placeholder="Teaching and modules"
                defaultValue={program?.data.teaching_and_modules || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[career_information]">Career information</label>
              <Field
                name="program[career_information]"
                as="textarea"
                placeholder="Career information"
                defaultValue={program?.data.career_information || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[language]">Language</label>
              <input
                name="program[language]"
                placeholder="Language"
                defaultValue={program?.data.language || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[employability]">Employability</label>
              <input
                name="program[employability]"
                placeholder="Employability"
                defaultValue={program?.data.employability || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[priority]">Priority</label>
              <input
                name="program[priority]"
                type="number"
                placeholder="Priority"
                defaultValue={program?.data.priority || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[employability_time]">Employability time(months)</label>
              <input
                name="program[employability_time]"
                type="number"
                placeholder="Employability time(months)"
                defaultValue={program?.data.employability_time || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[employability_score]">Employability score(%)</label>
              <input
                name="program[employability_score]"
                type="number"
                placeholder="Employability score(%)"
                defaultValue={program?.data.employability_score || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[period_type]">Period Type</label>
              <select name="program[period_type]" onChange={handleChange}>
                <option selected={String(program?.data.period_type) === 'nil'} value="nil" />
                <option selected={program?.data.period_type === 'semester'} value="semester">
                  Semester
                </option>
                <option selected={program?.data.period_type === 'trimester'} value="trimester">
                  Trimester
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[assessment_text]">Assessment Text</label>
              <Field
                name="program[assessment_text]"
                as="textarea"
                placeholder="Assessment Text"
                defaultValue={program?.data.assessment_text || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[description]">Description</label>
              <Field
                name="program[description]"
                as="textarea"
                placeholder="Description"
                defaultValue={program?.data.description || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[men_women_percentage]">
                Men Women percentage(write only mens)
              </label>
              <input
                name="program[men_women_percentage]"
                type="number"
                placeholder="Men Women percentage"
                defaultValue={program?.data.men_women_percentage || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[fee_cost_description]">Fee Cost description</label>
              <Field
                name="program[fee_cost_description]"
                as="textarea"
                placeholder="Fee Cost description"
                defaultValue={program?.data.fee_cost_description || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[application_fee]">Application fee</label>
              <Field
                name="program[application_fee]"
                type="number"
                placeholder="Application fee"
                defaultValue={program?.data.application_fee || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[application_fee_currency]">Application fee currency</label>
              <select
                name="program[application_fee_currency]"
                defaultValue={program?.data.application_fee_currency || 'nil'}
                onChange={handleChange}
              >
                <option value="nil" />
                <option value="USD">USD</option>
                <option value="RMB">RMB</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="NOK">NOK</option>
                <option value="HUF">HUF</option>
                <option value="CHF">CHF</option>
                <option value="MYR">MYR</option>
                <option value="AED">AED</option>
                <option value="SGD">SGD</option>
                <option value="PLN">PLN</option>
                <option value="SEK">SEK</option>
                <option value="ISK">ISK</option>
                <option value="BAM">BAM</option>
                <option value="BHD">BHD</option>
                <option value="CZK">CZK</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[deposit]">Deposit</label>
              <Field
                name="program[deposit]"
                type="number"
                placeholder="Deposit"
                defaultValue={program?.data.deposit || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[deposit_currency]">Deposit currency</label>
              <Field
                name="program[deposit_currency]"
                as="select"
                defaultValue={program?.data.deposit_currency || 'nil'}
              >
                <option value="nil"></option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </Field>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[is_visible]">Is visible</label>
              <select name="program[is_visible]" onChange={handleChange}>
                <option selected={String(program?.data.is_visible) === 'nil'} value="nil" />
                <option selected={String(program?.data.is_visible) === 'true'} value="true">
                  True
                </option>
                <option selected={String(program?.data.is_visible) === 'false'} value="false">
                  False
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[degree_type]">Degree type</label>
              <select name="program[degree_type]" onChange={handleChange}>
                <option selected={String(program?.data.degree_type) === 'nil'} value="nil" />
                <option selected={program?.data.degree_type === 'bachelors'} value="bachelors">
                  Bachelors
                </option>
                <option selected={program?.data.degree_type === 'masters'} value="masters">
                  Masters
                </option>
                <option selected={program?.data.degree_type === 'integrated'} value="integrated">
                  Integrated
                </option>
                <option selected={program?.data.degree_type === 'diploma'} value="diploma">
                  Diploma
                </option>
                <option
                  selected={program?.data.degree_type === 'postgraduate_certificate'}
                  value="postgraduate_certificate"
                >
                  Postgraduate Certificate
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[prefix]">Prefix</label>
              <select
                name="program[prefix]"
                onChange={handleChange}
                defaultValue={program?.data.prefix || ''}
              >
                <option value="nil" />
                <option value="MA">MA</option>
                <option value="MBA">MBA</option>
                <option value="MSc">MSc</option>
                <option value="MMA">MMA</option>
                <option value="PHD">PHD</option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="program[working_after_months]">Working after months</label>
              <Field
                name="program[working_after_months]"
                type="number"
                placeholder="Working after months"
                defaultValue={program?.data.working_after_months || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[working_after_percentage]">Woking after percentage(%)</label>
              <Field
                name="program[working_after_percentage]"
                type="number"
                placeholder="Woking after percentage"
                defaultValue={program?.data.working_after_percentage || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="program[requirement_document_description]">
                Requirement document description
              </label>
              <Field
                name="program[requirement_document_description]"
                as="textarea"
                placeholder="Requirement document description"
                defaultValue={program?.data.requirement_document_description || ''}
              />
            </div>
            <Text size={24} weight={800}>
              Study Options
            </Text>
            <FieldArray
              name="study_options"
              render={(arrayHelpers) => (
                <div>
                  {values.study_options && values.study_options.length > 0 ? (
                    values.study_options.map(
                      (s_option: CommonHash, index: number) =>
                        !s_option._destroy && (
                          <div key={index}>
                            <div className="d-flex gap-3 align-items-center mb-2">
                              <Text size={22}> Study option {index + 1} </Text>
                              <Button
                                type="button"
                                className={styles.deleteButton}
                                onClick={() => {
                                  if (id) {
                                    setFieldValue(`study_options[${index}]._destroy`, true);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`study_options[${index}].city_id`}>City</label>
                              <Select
                                isSearchable
                                classNamePrefix="react-select"
                                className={styles.filtersFormSelect}
                                placeholder="Search City"
                                options={cities?.map((c) => ({ label: c.name, value: c.id })) || []}
                                isLoading={citiesLoading}
                                onChange={({ value }: any) =>
                                  setFieldValue(`study_options[${index}].city_id`, value)
                                }
                                value={cities
                                  ?.map((city) => ({ label: city.name, value: city.id }))
                                  .find(
                                    (city) =>
                                      city.value ===
                                      (values.study_options[index].city_id || 'Search cities')
                                  )}
                              />
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`study_options[${index}].delivery`}>Delivery</label>
                              <select
                                name={`study_options[${index}].delivery`}
                                onChange={handleChange}
                                value={s_option.delivery}
                              >
                                <option
                                  value="nil"
                                  // selected={String(s_option.delivery) === 'nil'}
                                />
                                <option
                                  value="in_person" /*selected={parseInt(s_option.delivery) === 0} */
                                >
                                  In person
                                </option>
                                <option
                                  value="online" /*</select>selected={parseInt(s_option.delivery) === 1}*/
                                >
                                  Online
                                </option>
                                <option
                                  value="blended" /*selected={parseInt(s_option.delivery) === 2}*/
                                >
                                  Blended
                                </option>
                              </select>
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`study_options[${index}].schedule`}>Schedule</label>
                              <select
                                name={`study_options[${index}].schedule`}
                                onChange={handleChange}
                                value={s_option.schedule}
                              >
                                <option value="nil" />
                                <option value="full_time">Full time</option>
                                <option value="part_time">Part time</option>
                              </select>
                            </div>
                            <div className={styles.group}>
                              <label htmlFor="study_options[${index}].duration_in_months">
                                Duration in months
                              </label>
                              <Field
                                name={`study_options[${index}].duration_in_months`}
                                type="number"
                                placeholder="Duration in months"
                                defaultValue={s_option.duration_in_months}
                              />
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`study_options[${index}].status`}>Status</label>
                              <select
                                name={`study_options[${index}].status`}
                                onChange={handleChange}
                                value={s_option.status}
                              >
                                <option value="nil" />
                                <option value="active">Active</option>
                                <option value="archived">Archived</option>
                                <option value="expired">Expired</option>
                              </select>
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`study_options[${index}].external_id`}>
                                External id
                              </label>
                              <Field
                                name={`study_options[${index}].external_id`}
                                placeholder="External id"
                                defaultValue={s_option.external_id}
                              />
                            </div>
                            <Text
                              size={22}
                              weight={600}
                              inlineStyle={{ marginTop: '20px', marginBottom: '10px' }}
                            >
                              Tuition Fee
                            </Text>
                            <div className={styles.group}>
                              <label htmlFor={`study_options[${index}].tuition_fee.currency`}>
                                Currency
                              </label>
                              <select
                                name={`study_options[${index}].tuition_fee.currency`}
                                onChange={handleChange}
                                value={s_option.tuition_fee.currency}
                              >
                                <option value="nil" />
                                <option value="USD">USD</option>
                                <option value="RMB">RMB</option>
                                <option value="GBP">GBP</option>
                                <option value="EUR">EUR</option>
                                <option value="NOK">NOK</option>
                                <option value="HUF">HUF</option>
                                <option value="CHF">CHF</option>
                                <option value="MYR">MYR</option>
                                <option value="AED">AED</option>
                                <option value="SGD">SGD</option>
                                <option value="PLN">PLN</option>
                                <option value="SEK">SEK</option>
                                <option value="ISK">ISK</option>
                                <option value="BAM">BAM</option>
                                <option value="BHD">BHD</option>
                                <option value="CZK">CZK</option>
                                <option value="CAD">CAD</option>
                                <option value="AUD">AUD</option>
                              </select>
                            </div>
                            <div className={styles.group}>
                              <label
                                htmlFor={`study_options[${index}].tuition_fee.amount_domestic`}
                              >
                                Amount domestic
                              </label>
                              <Field
                                name={`study_options[${index}].tuition_fee.amount_domestic`}
                                defaultValue={s_option.tuition_fee?.amount_domestic}
                                type="number"
                              />
                            </div>
                            <div className={styles.group}>
                              <label
                                htmlFor={`study_options[${index}].tuition_fee.amount_international`}
                              >
                                Amount international
                              </label>
                              <Field
                                name={`study_options[${index}].tuition_fee.amount_international`}
                                defaultValue={s_option.tuition_fee?.amount_international}
                                type="number"
                              />
                            </div>
                            <Text
                              size={22}
                              weight={800}
                              inlineStyle={{ marginTop: '20px', marginBottom: '10px' }}
                            >
                              {' '}
                              Intake Dates{' '}
                            </Text>
                            <IntakeDatesForm
                              name={`study_options[${index}].intake_dates`}
                              studyOption={s_option}
                            />
                            <Button
                              type="button"
                              variant="success"
                              className="mt-3"
                              onClick={() =>
                                arrayHelpers.push({
                                  city_id: '',
                                  delivery: '',
                                  schedule: '',
                                  duration_in_months: '',
                                  status: '',
                                  external_id: '',
                                  tuition_fee: {
                                    currency: '',
                                    amount_domestic: '',
                                    amount_international: '',
                                  },
                                  intake_dates: [
                                    {
                                      date: '',
                                      deadline_dates: [
                                        {
                                          date: '',
                                          description: '',
                                        },
                                      ],
                                    },
                                  ],
                                })
                              }
                            >
                              + Study Option
                            </Button>
                          </div>
                        )
                    )
                  ) : (
                    <Button
                      type="button"
                      variant="success"
                      onClick={() =>
                        arrayHelpers.push({
                          city_id: '',
                          delivery: '',
                          schedule: '',
                          duration_in_months: '',
                          status: '',
                          external_id: '',
                          tuition_fee: {
                            currency: '',
                            amount_domestic: '',
                            amount_international: '',
                          },
                          intake_dates: [
                            {
                              date: '',
                              deadline_dates: [
                                {
                                  date: '',
                                  description: '',
                                },
                              ],
                            },
                          ],
                        })
                      }
                    >
                      + Study Option
                    </Button>
                  )}
                </div>
              )}
            />
            <button type="submit">Submit</button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ProgramForm;
